/** Share concurrent reads only. Resolve fetch at call time: native bridge installs after module evaluation. */
export function createServerInventoryLoader(fetcher: typeof fetch = (input, init) => globalThis.fetch(input, init)) {
  const pending = new Map<string, Promise<unknown[]>>()
  return function load<T extends { name: string }>(token: string, fresh = false): Promise<T[]> {
    if (!token) return Promise.reject(new Error('Oturum bulunamadı'))
    const existing = pending.get(token)
    if (existing && !fresh) return existing as Promise<T[]>
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 10_000)
    const request = (async () => {
      const read = () => fetcher('/api/servers', { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal })
      const mayRetry = () => !controller.signal.aborted
        && (typeof document === 'undefined' || document.visibilityState === 'visible')
        && (typeof navigator === 'undefined' || navigator.onLine !== false)
      let response: Response
      try {
        response = await read()
      } catch (error) {
        if (!(error instanceof TypeError) || !mayRetry()) throw error
        await new Promise(resolve => setTimeout(resolve, 250))
        if (!mayRetry()) throw error
        response = await read()
        // A transport retry consumes the single retry allowance.
        if (!response.ok) throw new Error(`Sunucu listesi alınamadı (HTTP ${response.status})`)
      }
      if ([502, 503, 504].includes(response.status) && mayRetry()) {
        await new Promise(resolve => setTimeout(resolve, 250))
        if (mayRetry()) response = await read()
      }
      if (!response.ok) throw new Error(`Sunucu listesi alınamadı (HTTP ${response.status})`)
      const rows: unknown = await response.json()
      if (!Array.isArray(rows) || rows.some(row => !row || typeof row !== 'object' || typeof row.name !== 'string')) {
        throw new Error('Sunucu envanteri geçersiz')
      }
      return rows
    })().finally(() => {
      clearTimeout(timer)
      if (pending.get(token) === request) pending.delete(token)
    })
    pending.set(token, request)
    return request as Promise<T[]>
  }
}

export const loadServerInventory = createServerInventoryLoader()
